import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useAuth } from "auth";
import merchant_services from "services/merchant_services";
import { getMerchantStatus } from "pages/issuer-page/useIssuerMerchants";
import { networkTypeSelector } from "store/userSliceHelpers";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import type { MerchantRecord } from "components/merchant-row/MerchantRow";
import type { SpecificMerchant } from "utils/AxiosUtil";
import type { NetworkType } from "utils/network";

/** Map the on-chain lookup shape onto the row model that MerchantRow renders. */
function toMerchantRecord(merchant: SpecificMerchant): MerchantRecord {
  return {
    merchantId: merchant.merchantId,
    merchantName: merchant.merchantName,
    merchantAddress: merchant.merchantAddress,
    // The recent list takes merchantName[0] unguarded; here the name can be empty on a
    // partially-registered merchant, so fall back rather than render "undefined".
    logoLetter: merchant.merchantName?.[0] ?? "?",
    balance: merchant.balance,
    merchantStatus: merchant.merchantStatus,
    status: getMerchantStatus(merchant.merchantStatus),
    signedUp: merchant.signedUp,
  };
}

export type MerchantLookupStatus =
  | "idle" // no lookup is running (disabled by the caller, or no auth yet)
  | "loading" // a request is in flight for the current merchant id
  | "found" // the chain returned a registered merchant
  | "not-found" // the chain answered, definitively, with no such merchant
  | "error"; // the lookup could not be completed (network/server error)

export interface UseMerchantLookupResult {
  status: MerchantLookupStatus;
  merchant: MerchantRecord | null;
  /** Re-run the lookup — used to recover from the `error` state. */
  refetch: () => void;
}

/** Shared with useMerchantNames so a list and a single lookup hit the same cache entry. */
export function merchantLookupQuery(
  networkType: NetworkType,
  merchantId: string,
  token: string | undefined,
) {
  return {
    // The sequence block of a merchantId is per-network, so the same id can name a
    // different merchant on testnet and mainnet — the network belongs in the key.
    // The looked-up merchant carries a BALANCE, so the result is token-scoped.
    queryKey: ["merchant-lookup", networkType, merchantId, token],
    queryFn: () =>
      merchant_services.lookupMerchant(merchantId, networkType, token),
    retry: false,
    staleTime: 60_000,
  };
}

/**
 * Looks a merchant up on-chain — the fallback used when it isn't among the recent
 * merchants already loaded. Gated by `enabled` so the request only fires when the
 * caller has decided a lookup is warranted (e.g. the local list came up empty for a
 * complete merchant id).
 *
 * Crucially, a request that fails (network/5xx) surfaces as `error`, never as
 * `not-found` — only a successful response describing an unregistered id is a true
 * miss, so the UI never tells the operator a merchant doesn't exist when the lookup
 * simply couldn't reach the chain.
 */
export function useMerchantLookup(
  merchantId: string,
  enabled: boolean,
): UseMerchantLookupResult {
  const { user } = useAuth();
  const networkType = useSelector(networkTypeSelector);
  const { param: token } = useSettlementToken();
  const normalized = merchantId.trim();
  const active = !!user && enabled && normalized.length > 0;

  const query = useQuery<SpecificMerchant | null>({
    ...merchantLookupQuery(networkType, normalized, token),
    enabled: active,
  });

  const merchant =
    active && query.data ? toMerchantRecord(query.data) : null;

  let status: MerchantLookupStatus;
  if (!active) {
    status = "idle";
  } else if (query.isFetching) {
    status = "loading";
  } else if (query.isError) {
    status = "error";
  } else if (query.isSuccess) {
    status = merchant ? "found" : "not-found";
  } else {
    // Active but not yet fetching — the tick between enabling and the request
    // actually starting. Treat as loading so we never flash a stale outcome.
    status = "loading";
  }

  return { status, merchant, refetch: query.refetch };
}
